import React, { useState, useEffect, useCallback } from 'react'
import { Button, Table, Modal, Form, InputNumber, Input, Select, Space, message as antMessage } from 'antd'
import { EditOutlined, DeleteOutlined, PauseCircleOutlined, CaretRightOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons'
import api from '../../api'

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}
const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

export default function AccountMonitorTab() {
  const [tasks, setTasks] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [noteFilter, setNoteFilter] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState(undefined)
  const [form] = Form.useForm()

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [taskRes, accList] = await Promise.all([
        api.listMonitorTasks(),
        api.getAccounts()
      ])
      setTasks(taskRes.tasks || [])
      setAccounts((accList || []).filter(a => !a.is_login_session))
    } catch {
      antMessage.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const findAccount = (accountId) => accounts.find(a => a.account_id === accountId) || null

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        frequency: values.frequency || 12,
        account_id: values.account_id || null
      }
      if (editingTask) {
        const res = await api.updateMonitorTask({ task_id: editingTask.task_id, ...payload })
        if (res.success) {
          antMessage.success('修改成功')
          setModalOpen(false)
          setEditingTask(null)
          form.resetFields()
          loadData()
        } else {
          antMessage.error(res.message || '修改失败')
        }
      } else {
        const res = await api.createMonitorTask(payload)
        if (res.success) {
          antMessage.success('添加成功')
          setModalOpen(false)
          form.resetFields()
          loadData()
        } else {
          antMessage.error(res.message || '添加失败')
        }
      }
    } catch {}
  }

  const handleDelete = (task_id) => {
    Modal.confirm({
      title: '删除任务',
      content: '确定要删除该监控任务吗？',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await api.deleteMonitorTask(task_id)
        antMessage.success('已删除')
        loadData()
      }
    })
  }

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'running' ? 'stopped' : 'running'
    await api.updateMonitorTask({ task_id: task.task_id, status: newStatus })
    antMessage.success(newStatus === 'running' ? '已启动' : '已停止')
    loadData()
  }

  const openEdit = (task) => {
    setEditingTask(task)
    form.setFieldsValue({
      frequency: task.frequency,
      account_id: task.account_id
    })
    setModalOpen(true)
  }

  const openCreate = () => {
    setEditingTask(null)
    form.resetFields()
    setModalOpen(true)
  }

  let filtered = tasks
  if (noteFilter) {
    const kw = noteFilter.toLowerCase()
    filtered = filtered.filter(t => (t.task_note || '').toLowerCase().includes(kw) || (t.task_name || '').toLowerCase().includes(kw))
  }
  if (accountFilter) {
    const kw = accountFilter.toLowerCase()
    filtered = filtered.filter(t => {
      const acc = findAccount(t.account_id)
      if (!acc) return false
      return (acc.nickname || '').toLowerCase().includes(kw) || (acc.remark || '').toLowerCase().includes(kw)
    })
  }
  if (statusFilter) {
    filtered = filtered.filter(t => t.status === statusFilter)
  }

  const columns = [
    {
      title: '监控账号', dataIndex: 'account_id', key: 'account', width: 180, resizable: true,
      render: (accountId) => {
        const acc = findAccount(accountId)
        if (!acc) return <span style={{ color: '#bbb' }}>未绑定</span>
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', overflow: 'hidden',
              background: '#f0f0f0', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {acc.avatar_url ? (
                <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none' }} />
              ) : (
                <span style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>
                  {(acc.nickname || PLATFORM_NAMES[acc.platform] || '?')[0]}
                </span>
              )}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{acc.remark || acc.nickname || PLATFORM_NAMES[acc.platform] || '未命名'}</div>
              <div style={{ fontSize: 11, color: '#999' }}>{PLATFORM_NAMES[acc.platform]}</div>
            </div>
          </div>
        )
      }
    },
    {
      title: '监控状态', dataIndex: 'status', key: 'status', width: 100, resizable: true,
      render: (status) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: status === 'running' ? '#52c41a' : '#d9d9d9',
            display: 'inline-block'
          }} />
          {status === 'running' ? '运行中' : '停止'}
        </span>
      )
    },
    {
      title: '最后运行时间', dataIndex: 'last_run_time', key: 'lastRun', width: 180, resizable: true,
      render: v => v ? new Date(v).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-') : '-'
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created', width: 180, resizable: true,
      render: v => v ? new Date(v).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-') : '-'
    },
    {
      title: '操作', key: 'action', width: 180, resizable: true,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EditOutlined />}
            onClick={() => openEdit(record)}>修改</Button>
          <Button type="link" size="small" icon={<DeleteOutlined />}
            danger onClick={() => handleDelete(record.task_id)}>删除</Button>
          <Button type="link" size="small"
            icon={record.status === 'running' ? <PauseCircleOutlined /> : <CaretRightOutlined />}
            onClick={() => handleToggleStatus(record)}
            style={{ color: record.status === 'running' ? '#faad14' : '#52c41a' }}
          >
            {record.status === 'running' ? '停止' : '启动'}
          </Button>
        </Space>
      )
    }
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff', padding: 16 }}>
      <div style={{ flexShrink: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Input
          placeholder="任务备注关键词"
          allowClear
          size="small"
          style={{ width: 170 }}
          value={noteFilter}
          onChange={e => setNoteFilter(e.target.value)}
        />
        <Input
          placeholder="账号昵称/备注关键词"
          allowClear
          size="small"
          style={{ width: 180 }}
          value={accountFilter}
          onChange={e => setAccountFilter(e.target.value)}
        />
        <Select
          placeholder="筛选运行状态"
          allowClear
          size="small"
          style={{ width: 140 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'running', label: '运行中' },
            { value: 'stopped', label: '已停止' }
          ]}
        />
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
        <Button type="primary" size="small" icon={<PlusOutlined />}
          style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
          onClick={openCreate}>
          添加监控
        </Button>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <Table
          columns={columns}
          dataSource={filtered.map(t => ({ ...t, key: t.task_id }))}
          loading={loading}
          pagination={false}
          size="middle"
          showSorterTooltip={false}
        />
      </div>

      <Modal
        title={editingTask ? '修改监控' : '添加监控'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingTask(null) }}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="frequency" label="监控频率（小时）" initialValue={12}>
            <InputNumber min={1} max={168} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
